import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  RiArrowRightSLine,
  RiExternalLinkLine,
  RiFileCopyLine,
} from "@remixicon/react";
import { useAuth } from "@/contexts/AuthContext";
import { allNavItems } from "@/components/dashboard/nav-config";
import { Button } from "@/components/ui/button";

const segmentLabels: Record<string, string> = {
  new: "New",
  edit: "Edit",
  variants: "Variants",
  payments: "Payments",
};

type Crumb = { label: string; href?: string };

function buildCrumbs(pathname: string): Crumb[] {
  const clean = pathname.replace(/\/+$/, "") || "/dashboard";
  const match = [...allNavItems]
    .sort((a, b) => b.href.length - a.href.length)
    .find((item) => clean === item.href || clean.startsWith(`${item.href}/`));

  if (!match) return [{ label: "Dashboard" }];

  const crumbs: Crumb[] = [];
  if (match.href !== "/dashboard" && match.href.startsWith("/dashboard")) {
    crumbs.push({ label: "Dashboard", href: "/dashboard" });
  }
  crumbs.push({ label: match.name, href: match.href });

  if (match.href === "/dashboard/settings") return crumbs;

  const rest = clean.slice(match.href.length).split("/").filter(Boolean);
  let path = match.href;
  rest.forEach((segment) => {
    path += `/${segment}`;
    crumbs.push({ label: segmentLabels[segment] ?? "Details", href: path });
  });

  const last = crumbs[crumbs.length - 1];
  crumbs[crumbs.length - 1] = { label: last.label };
  return crumbs;
}

export function Topbar() {
  const { pathname } = useLocation();
  const { currentStore } = useAuth();
  const crumbs = buildCrumbs(pathname);
  const storeLink = currentStore?.url ? `https://sqale.shop/${currentStore.url}` : "";

  const copyLink = () => {
    navigator.clipboard
      .writeText(storeLink)
      .then(() => toast.success("Store link copied", { description: storeLink }))
      .catch(() => toast.error("Couldn't copy the link"));
  };

  return (
    <div className="sticky -top-8 z-20 -mx-8 -mt-8 mb-6 hidden h-14 items-center justify-between gap-4 border-b border-border bg-card/85 px-8 backdrop-blur-md md:flex">
      <nav aria-label="Breadcrumb" className="min-w-0">
        <ol className="flex min-w-0 items-center gap-1 text-[13px] font-medium">
          {crumbs.map((crumb, index) => (
            <Fragment key={`${crumb.label}-${index}`}>
              {index > 0 ? (
                <RiArrowRightSLine className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              ) : null}
              <li className="min-w-0">
                {crumb.href ? (
                  <Link
                    to={crumb.href}
                    className="truncate rounded-md px-1.5 py-1 text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span aria-current="page" className="truncate px-1.5 py-1 font-semibold text-foreground">
                    {crumb.label}
                  </span>
                )}
              </li>
            </Fragment>
          ))}
        </ol>
      </nav>

      {storeLink ? (
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={copyLink}
            className="h-8 rounded-full px-3 text-[13px] font-semibold text-muted-foreground"
          >
            <RiFileCopyLine className="h-3.5 w-3.5" />
            Copy link
          </Button>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="h-8 rounded-full px-3.5 text-[13px] font-semibold"
          >
            <a href={storeLink} target="_blank" rel="noopener noreferrer">
              View store
              <RiExternalLinkLine className="h-3.5 w-3.5" />
            </a>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
